import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import type { ProductPage } from '../data/productPages';
type PageLightboxProps = {
  pages: ProductPage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function PageLightbox({
  pages,
  index,
  onClose,
  onChange
}: PageLightboxProps) {
  const [zoomed, setZoomed] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const open = index !== null;
  const go = (dir: 1 | -1) => {
    if (index === null) return;
    setZoomed(false);
    onChange((index + dir + pages.length) % pages.length);
  };
  useEffect(() => {
    if (!open) return;
    setZoomed(false);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const page = index !== null ? pages[index] : null;
  return <AnimatePresence data-id="element-124" data-mp="hg9ik75yg5a1">
      {page && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={`Vista ampliada: ${page.label}`} className="fixed inset-0 z-[100] flex flex-col bg-navy-deep/95" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-125" data-mp="mhywbukkax1t">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6" data-id="element-126" data-mp="2sy7s7hlkpaz">
            <p className="text-sm font-semibold text-white" data-id="element-127" data-mp="1643xw433g6kk">
              {page.label}
              <span className="ml-2 font-normal text-white/50" data-id="element-128" data-mp="161r6z1fcylvd">
                {index + 1} / {pages.length}
              </span>
            </p>
            <div className="flex items-center gap-2" data-id="element-129" data-mp="1fu6pq0yhbehb">
              <button type="button" onClick={() => setZoomed(z => !z)} aria-label={zoomed ? 'Alejar imagen' : 'Acercar imagen'} aria-pressed={zoomed} className="grid h-10 w-10 place-items-center rounded-full border border-navy-line text-white transition-colors duration-150 hover:bg-navy-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-130" data-mp="1fvq6rtryltk5">
                {zoomed ? <ZoomOutIcon className="h-5 w-5" data-id="element-131" data-mp="s2rl21j8cl2w" /> : <ZoomInIcon className="h-5 w-5" data-id="element-132" data-mp="27l310x4w5b2h" />}
              </button>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar vista ampliada" className="grid h-10 w-10 place-items-center rounded-full bg-gold text-gold-ink transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-133" data-mp="27n61yc7l0olz">
                <XIcon className="h-5 w-5" data-id="element-134" data-mp="216xklkj8ezz1" />
              </button>
            </div>
          </div>

          <div className="relative flex-1 overflow-auto" onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }} data-id="element-135" data-mp="2660f4b1l5nsr">
            <div className={`flex min-h-full justify-center px-4 pb-6 ${zoomed ? 'items-start' : 'items-center'}`} onClick={e => {
          if (e.target === e.currentTarget) onClose();
        }} data-id="element-136" data-mp="2yrtgqhb5awdy">
              <motion.img key={page.src} src={page.src} alt={page.alt} onClick={() => setZoomed(z => !z)} initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.22,
            ease
          }} className={`rounded-xl shadow-card ${zoomed ? 'w-full max-w-[1100px] cursor-zoom-out' : 'max-h-[calc(100vh-96px)] w-auto max-w-full cursor-zoom-in object-contain'}`} data-id="element-137" data-mp="3cqt6xteb3jc8" />
            </div>
          </div>

          <button type="button" onClick={() => go(-1)} aria-label="Página anterior" className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-gold text-gold-ink shadow-lift transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-6" data-id="element-138" data-mp="2cn3lblpe1a5j">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-139" data-mp="2qla8o7wpnrqy" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Página siguiente" className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-gold text-gold-ink shadow-lift transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-6" data-id="element-140" data-mp="2qixg70tbtyzn">
            <ChevronRightIcon className="h-5 w-5" data-id="element-141" data-mp="3cr2z6vjjfirf" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}